const PROJECT_ID = 'rayze-dev-e25ce' as const

export const SECRET_KEYS = [
  'GOOGLE_GEOCODE_API_KEY',
  'STRIPE_PUBLISHABLE_KEY',
  'STRIPE_CLIENT_ID',
  'COT_ACCOUNT_ID',
  'VM_API_KEY',
  'VM_API_URL',
] as const

export type SecretKey = (typeof SECRET_KEYS)[number]

export const SECRET_PATHS: Record<SecretKey, string> = {
  GOOGLE_GEOCODE_API_KEY: `projects/${PROJECT_ID}/secrets/GOOGLE_GEOCODE_API_KEY/versions/latest`,
  STRIPE_PUBLISHABLE_KEY: `projects/${PROJECT_ID}/secrets/STRIPE_PUBLISHABLE_KEY/versions/latest`,
  STRIPE_CLIENT_ID: `projects/${PROJECT_ID}/secrets/STRIPE_CLIENT_ID/versions/latest`,
  COT_ACCOUNT_ID: `projects/${PROJECT_ID}/secrets/COT_ACCOUNT_ID/versions/latest`,
  VM_API_KEY: `projects/${PROJECT_ID}/secrets/VM_API_KEY/versions/latest`,
  VM_API_URL: `projects/${PROJECT_ID}/secrets/VM_API_URL/versions/latest`,
}

// ---------------------------------------------------------------------------
// Module-level cache (populated by initializeSecrets)
// ---------------------------------------------------------------------------

let cache: Record<string, string> | null = null

/**
 * Internal setter used by `initializeSecrets()` to populate the cache.
 */
export function setCache(values: Record<string, string>): void {
  cache = values
}

// ---------------------------------------------------------------------------
// Synchronous accessors
// ---------------------------------------------------------------------------

/**
 * Synchronous getter for a resolved secret value.
 *
 * @returns The secret string, or `undefined` if `initializeSecrets()` has not
 *          been called yet or the key was not resolved.
 */
export function getSecret(key: SecretKey): string | undefined {
  return cache?.[key]
}

/**
 * Synchronous getter that throws when the requested secret is unavailable.
 * Use this in places where a missing secret is a fatal configuration error.
 *
 * @throws `Error` if `initializeSecrets()` has not been called or the key is
 *         absent from the cache.
 */
export function requireSecret(key: SecretKey): string {
  const value = getSecret(key)
  if (value === undefined) {
    throw new Error(
      `Required secret "${key}" is not available. ` +
        'Ensure initializeSecrets() has been called and completed successfully.',
    )
  }
  return value
}

// ---------------------------------------------------------------------------
// Geocode initialization
// ---------------------------------------------------------------------------

/**
 * Lazy-load the Geocode library and set its API key from the cached secrets.
 * Must be called after `initializeSecrets()` has resolved successfully.
 */
export async function initializeGeocode(): Promise<void> {
  const apiKey = requireSecret('GOOGLE_GEOCODE_API_KEY')
  const GeocodeModule = await import('react-geocode')
  GeocodeModule.setKey(apiKey)
}
